import { StackActions } from '@react-navigation/native';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { useCallback, useMemo } from 'react';

import { AuthRoute } from '@/features/authorization/navigation/authRoute';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { ErrorScreen } from '@/features/authorization/screens/ErrorScreen';
import { InvalidVersionScreen } from '@/features/authorization/screens/InvalidVersionScreen';
import { LoginScreen } from '@/features/authorization/screens/LoginScreen';
import { RegistrationScreen } from '@/features/authorization/screens/RegistrationScreen';
import { RequiredActionScreen } from '@/features/authorization/screens/RequiredActionScreen';
import { useRegistrationStore } from '@/features/authorization/store/useRegistrationStore';
import { AuthStep } from '@/features/authorization/types/step';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { AppStack } from '@/navigation/stacks/appStack';
import { NavBarType } from '@/navigation/types/navbar';

/**
 * Todo: currently there are some issues with using form sheets as presentation for the screens
 * - On Android: form sheet can be closed with swipe down
 * - On iOS: the save area of the view is causing huge spacing on the top
 * - On both: Alerts are being displayed behind form sheet (so far only containedModal works fine)
 * Todo: check in future if the issues are easy fixable and use form sheet with the passed configuration
 */

export const useAuthRouting = () => {
  const { logout } = useAuth();
  const step = useRegistrationStore((state) => state.step);

  const navigation = useAppNavigation();

  /**
   * Todo Future: like during registration flow show an alert when user cancels accepting legals or cancels the version check
   * Or move whole nav bar from registration to here
   */
  const handleCancel = useCallback(() => {
    navigation.dispatch(StackActions.popToTop());
    logout();
  }, [logout, navigation]);

  const modalOptions: NativeStackNavigationOptions = useMemo(
    () => ({
      header: () => (
        <NavigationBar
          type={NavBarType.MODAL_WITH_CLOSE}
          onBackButtonPress={handleCancel}
        />
      ),
      headerShown: true,
      presentation: 'containedModal',
      sheetAllowedDetents: [0.9],
      sheetCornerRadius: 20,
      animation: 'fade',
      gestureEnabled: false
    }),
    [handleCancel]
  );

  const invalidVersionScreen = (
    <AppStack.Screen
      key={AuthRoute.InvalidVersion}
      name={AuthRoute.InvalidVersion}
      component={InvalidVersionScreen}
      options={{ headerShown: false }}
    />
  );

  const sharedScreens = [
    <AppStack.Screen
      key={AuthRoute.Error}
      name={AuthRoute.Error}
      component={ErrorScreen}
      options={{ headerShown: false }}
    />,
    invalidVersionScreen
  ];

  const authScreens = [
    <AppStack.Screen
      key={AuthRoute.Login}
      name={AuthRoute.Login}
      component={LoginScreen}
      options={{ headerShown: false }}
    />,
    ...sharedScreens
  ];

  const registrationScreens = [
    <AppStack.Screen
      key={AuthRoute.Registration}
      name={AuthRoute.Registration}
      component={RegistrationScreen}
      initialParams={{ step: step ?? AuthStep.PROFILE }}
      options={modalOptions}
    />,
    <AppStack.Screen
      key={AuthRoute.RequiredAction}
      name={AuthRoute.RequiredAction}
      component={RequiredActionScreen}
      options={modalOptions}
    />,
    ...sharedScreens
  ];

  return { authScreens, registrationScreens, invalidVersionScreen };
};
